import jsqr from 'jsqr';
import { type RefObject, useEffect, useRef, useState } from 'react';

const DEBOUNCE_MS = 100;

interface ScanState {
  decodedValue: string | null;
  debounceTimer: ReturnType<typeof setTimeout> | null;
}

function processFrame(
  ctx: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
  video: HTMLVideoElement,
): string | null {
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;
  ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const result = jsqr(imageData.data, imageData.width, imageData.height);
  return result?.data ?? null;
}

function handleDetection(
  data: string,
  state: ScanState,
  onDecode: (value: string) => void,
  setDecoded: (v: boolean) => void,
) {
  if (state.debounceTimer !== null) {
    clearTimeout(state.debounceTimer);
    state.debounceTimer = null;
  }
  setDecoded(true);
  if (state.decodedValue !== data) {
    state.decodedValue = data;
    onDecode(data);
  }
}

function handleNoDetection(state: ScanState, setDecoded: (v: boolean) => void) {
  if (state.decodedValue !== null && state.debounceTimer === null) {
    state.debounceTimer = setTimeout(() => {
      state.decodedValue = null;
      setDecoded(false);
      state.debounceTimer = null;
    }, DEBOUNCE_MS);
  }
}

export function useQrScanner(
  videoRef: RefObject<HTMLVideoElement | null>,
  onDecode: (qrUuid: string) => void,
  isActive: boolean,
): { error: string | null; decoded: boolean } {
  const [error, setError] = useState<string | null>(null);
  const [decoded, setDecoded] = useState(false);
  const onDecodeRef = useRef(onDecode);

  useEffect(() => {
    onDecodeRef.current = onDecode;
  }, [onDecode]);

  useEffect(() => {
    if (!isActive) {
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      setError('NO_MEDIA_DEVICES');
      return;
    }

    let cancelled = false;
    let localStream: MediaStream | null = null;
    let animationId: number | null = null;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      setError('CANVAS_CONTEXT_FAILED');
      return;
    }

    setError(null);
    setDecoded(false);
    const state: ScanState = { decodedValue: null, debounceTimer: null };

    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: { ideal: 'environment' } } })
      .then((stream) => {
        if (cancelled) {
          for (const track of stream.getTracks()) {
            track.stop();
          }
          return;
        }
        localStream = stream;
        const video = videoRef.current;
        if (video) {
          video.srcObject = stream;
          video.play().catch(() => {
            // Autoplay rejection is non-fatal
          });
        }

        const tick = () => {
          if (cancelled || !videoRef.current) {
            return;
          }
          const v = videoRef.current;
          if (v.readyState === v.HAVE_ENOUGH_DATA) {
            const data = processFrame(ctx, canvas, v);
            if (data) {
              handleDetection(data, state, onDecodeRef.current, setDecoded);
            } else {
              handleNoDetection(state, setDecoded);
            }
          }
          animationId = requestAnimationFrame(tick);
        };

        animationId = requestAnimationFrame(tick);
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'SCAN_INIT_FAILED');
        }
      });

    return () => {
      cancelled = true;
      if (animationId !== null) {
        cancelAnimationFrame(animationId);
      }
      if (state.debounceTimer !== null) {
        clearTimeout(state.debounceTimer);
      }
      if (localStream) {
        for (const track of localStream.getTracks()) {
          track.stop();
        }
      }
    };
  }, [isActive, videoRef]);

  return { error, decoded };
}
